<template>
	<v-card class="my-profile-page page pt-5" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader v-if="this.isLoading" v-bind="attrs" type="card" />
			<v-card-text v-else-if="this.profile">
				<v-card class="my-profile-page__renew-section" flat v-if="user && renewUrl">
					<v-card-text class="d-flex justify-space-between align-center">
						<p class="my-profile-page__renew-section__label">
							{{ renewLabel }}
						</p>
						<a
							class="text-decoration-none"
							:href="renewUrl"
							target="_blank"
							@click="handleRenewButtonClick"
						>
							<button
								type="button"
								id="profile-full__renew-button"
								class="my-profile-page__renew-section__button"
							>
								{{ renewButtonName }}
							</button>
						</a>
					</v-card-text>
				</v-card>
				<v-card class="my-profile-page__section" flat>
					<SponsorBadge
						v-if="userFlag"
						:text="userFlag.flag_name"
						:top="'0px'"
						:right="'0px'"
						:iconSize="10"
						:iconColor="'#ffffff'"
						:background="userFlag.flag_bg_color"
						:color="userFlag.flag_txt_color"
					/>
					<div class="my-profile-page__info-wrapper">
						<Avatar
							:avatar="avatarPath"
							:size="120"
							border-radius="square"
							class="my-profile-page__avatar mr-4"
              :defaultAvatar="'speaker'"
              :color="'#ffffff'"
						/>

						<div class="my-profile-page__info">
							<h1 class="my-profile-page__info-username">{{ userName }}</h1>
							<div class="d-flex flex-column">
								<span v-if="companyName" class="my-profile-page__info-company mb-1">
									{{ companyName }}
								</span>
								<span v-if="heldPosition" class="my-profile-page__info-position">
									{{ heldPosition }}
								</span>
								<span v-if="location" class="my-profile-page__info-location">
									<Icon
										type="pin"
										color="var(--location-color)"
										:size="16"
										:pr="6"
									/>
									<span>{{ location }}</span>
								</span>
							</div>
						</div>
					</div>
				</v-card>
				<div
					v-for="(section, sIndex) in allowSections"
					:key="sIndex"
					class="my-profile-page__section"
				>
					<h3 class="my-profile-page__section-title">{{ section[0].section }}</h3>
					<template v-if="section[0].section === 'Network'">
						<a
							v-for="item in section"
							:key="item.field"
							:href="item.value"
							target="_blank"
							class="my-profile-page__network-link mt-2"
						>
							<div class="my-profile-page__network-link__box d-flex align-center">
								<Icon
									color="var(--button-icon-tint)"
									:size="24"
									:type="networkFieldType(item.type)"
								/>
								<span class="my-profile-page__network-link-text ml-2">
									{{ item.value }}
								</span>
							</div>
						</a>
					</template>
					<template v-else>
						<div v-for="(field, fIndex) in section" :key="field.field">
							<div
								v-if="field.type === 'email'"
								class="my-profile-page__section__phone-box d-flex flex-nowrap align-center"
							>
								<Icon
									type="email"
									color="var(--button-icon-tint)"
									:pr="8"
									:size="24"
								/>
								<a
									:href="'mailto:' + field.value"
									class="text-decoration-none"
									style="color: var(--link)"
								>
									{{ field.value }}
								</a>
							</div>
							<div
								v-else-if="field.type === 'phone'"
								class="my-profile-page__section__phone-box py-3 d-flex"
							>
								<Icon
									type="phone"
									color="var(--button-icon-tint)"
									:pr="8"
									:size="24"
								/>
								<a
									@click="copyPhone(field.value)"
									href="javascript:void(0)"
									style="color: var(--link)"
								>
									{{ field.value }}
								</a>
							</div>
							<v-card-text v-else-if="field.type === 'html'" v-html="field.value" />
							<div v-else class="mt-3">
								<span class="my-profile-page__section-label"
									>{{ field.title }}:</span
								>
								<span class="my-profile-page__section-text">{{ field.value }}</span>
							</div>
						</div>
					</template>
				</div>

				<v-skeleton-loader width="555" v-bind="attrs" type="card" v-if="false">
				</v-skeleton-loader>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import Avatar from '@/components/Avatar'
import PageBlocked from '@/components/PageBlocked'
import Block from '@/mixins/Block.js'
import twitterLogo from '@/assets/images/twitterX-logo.svg'
import Dwh from '@/mixins/Dwh'
import SponsorBadge from '@/components/SponsorBadge.vue'
import LocalStorageService from '@/services/local-storage.service'

export default {
	name: 'ProfilePage',
	mixins: [Block, Dwh],
	components: {
		SponsorBadge,
		PageBlocked,
		Avatar,
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated', 'isLoading','UserLogInCategories']),
		...mapGetters('settings', ['user', 'backUser', 'appCore']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),
    additionalUserSettings() {
      const raw = LocalStorageService.get('additionalLoginSettings')

      let parsed = null
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      } catch (e) {
        console.warn('Invalid LS additionalLoginSettings', raw)
        return {}
      }

      if (!parsed?.user_category_id) return {}

      const category = this.UserLogInCategories
          ?.find(c => c.user_category_id === parsed.user_category_id)

      return category?.additional_settings || {}
    },
		renewLabel() {
			const { renew_area_text: value } = this.additionalUserSettings || {}
			return value
		},
		renewButtonName() {
			const { renew_button_name: value } = this.additionalUserSettings || {}
			return value
		},
		renewUrl() {
			const { renew_url: value } = this.additionalUserSettings || {}
			return value
		},

		allowSections() {
			return this.profile.private
				.filter((field) => field.value)
				.reduce((acc, current) => {
					if (!acc.length || acc[acc.length - 1][0].section !== current.section) {
						acc.push([current])
					} else {
						acc[acc.length - 1].push(current)
					}
					return acc
				}, [])
		},
		networkFields() {
			return this.profile.private.filter(
				(field) => field.section === 'Network' && field.value
			)
		},
		profileFieldsType() {
			if (!this.user) {
				return 'public'
			}

			const { view_profile_link: viewProfileLink } = this.user

			return this.getTypeFieldsFromUrl(viewProfileLink)
		},
		isVerticalTabs() {
			return this.$vuetify.breakpoint.mdAndUp
		},

		// short profile
		location() {
			const { location_profile_row: location = {} } = this.profile
			return location
		},
		userName() {
			const { visible_username: name } = this.profile
			return name
		},
		avatarPath() {
			const { user_logo: userLogo } = this.profile
			return userLogo
		},
		heldPosition() {
			const { third_profile_row: heldPosition } = this.profile
			return heldPosition
		},
		companyName() {
			const { second_profile_row: company } = this.profile
			return company
		},
		userFlag() {
			const { userFlag: flag } = this.profile || {}

			return flag
		},
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert', 'setPageTitle']),
		...mapActions('settings', ['getUserSettings']),
    ...mapActions('auth', ['getLogInCategory']),
		handleRenewButtonClick() {
			this.sendTrigger({
				category: 'Profile',
				action: 'click_renew_button',
				module: 'Profile',
			})
		},
		onTabSwitch() {
			this.successMessage = false
			this.errorMessage = false
		},
		normalizeSettings(item) {
			const { setting, value } = item

			let settings = {}
			settings[setting.name] = value !== '0'

			return settings
		},
		formatUrl(url) {
			if (!url) {
				return ''
			}

			if (!url.startsWith('http://') && !url.startsWith('https://')) {
				if (url.startsWith('www.')) {
					url = 'https://' + url
				} else {
					url = 'https://www.' + url
				}
			}
			return url
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '')

				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		dateCreated(originalDate) {
			return new Date(originalDate * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		},
		getTypeFieldsFromUrl(url) {
			const parts = url.split('/')
			return parts[parts.length - 1]
		},
		fieldClass(allowFields, index) {
			if (index === 0 || allowFields[index].section !== allowFields[index - 1].section) {
				return 'mt-8'
			} else if (
				!allowFields[index + 1] ||
				allowFields[index + 1].section !== allowFields[index].section
			) {
				return 'mb-8'
			} else {
				return ''
			}
		},
		networkFieldType(type) {
			if (type === 'url') {
				return 'link'
			} else {
				return type
			}
		},
	},
	data() {
		return {
			searchIcon,
			twitterLogo,
			successMessage: false,
			errorMessage: false,
			attrs: {
				elevation: 0,
			},
			socialFieldNames: ['user_linkedin', 'user_facebook', 'user_twitter', 'user_custom_url'],
			phoneFieldNames: ['user_work_phone', 'user_cell_phone'],
			pageBlockedName: 'profile',
		}
	},
	mounted() {
		this.setPageTitle('My Profile')
	},
	beforeDestroy() {
		this.setPageTitle('')
	},
	created() {
		if (!this.user) {
			this.getUserSettings()
		}
     this.getLogInCategory()
  },
}
</script>

<style lang="scss">
.my-profile-page {
	background-color: transparent !important;
	&__renew-section {
		max-width: 555px;
		border-radius: 8px !important;
		margin-bottom: 8px;
		border: 1px solid #e2e2e2 !important;
		padding: 12px;
		background-color: #ffffff !important;
		font-family: 'Inter', sans-serif;
		&__label {
			font-weight: 500;
			font-size: 16px;
			font-family: 'Inter', sans-serif;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color);
		}
		&__button {
			width: 148px;
			height: 46px;
			border-radius: 8px;
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1.5px solid var(--button-secondary-background-color);
			color: var(--button-secondary-background-color);
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
		}
	}

	&__section {
		max-width: 555px;
		border-radius: 8px !important;
		margin-bottom: 8px;
		border: 1px solid #e2e2e2 !important;
		padding: 12px;
		background-color: #ffffff !important;
		font-family: 'Inter', sans-serif;
		&__phone-box {
			padding: 12px;
			border-radius: 8px;
			background: #f3f3f6;
			margin-top: 8px;
		}

		& > div:not(:last-child) > a.my-profile-page__network {
			margin-bottom: 12px;
		}

		&-title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
			margin-bottom: 12px;
		}

		&-label {
			font-family: 'Inter', sans-serif;
			font-weight: 600;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
			margin-right: 8px;
		}

		&-text {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}

	&__network {
		a:not(:last-child) {
			margin-bottom: 8px;
		}

		&-link {
			text-decoration: none;
			display: block;
			&__box {
				padding: 12px;
				border-radius: 8px;
				background-color: #f3f3f6;
				align-items: center;
			}

			&-text {
				font-size: 14px;
				line-height: 17.07px;
				color: var(--link);
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: #4a4a4a;
			margin-bottom: 4px;
		}
	}

	&__info {
		display: flex;
		flex-direction: column;
		justify-content: center;
		font-family: 'Inter', sans-serif;
		width: 100%;

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;

			@media (max-width: 480px) {
				flex-direction: column;
				align-items: center;
			}
		}

		&-username {
			font-size: 24px;
			line-height: 100%;
			font-weight: 700;
			color: var(--username-category-title) !important;
			margin-bottom: 8px;

			@media (max-width: 480px) {
				margin-top: 8px;
				text-align: center;
			}
		}

		&-company,
		&-position {
			font-size: 16px;
			line-height: 100%;
			font-weight: 400;
			color: #4a4a4a;
			width: 100%;
			white-space: normal;
			word-break: break-word;

			@media (max-width: 480px) {
				text-align: center;
			}
		}

		&-location {
			font-size: 13px;
			line-height: 100%;
			font-weight: 500;
			color: var(--location-color);
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
			margin-top: 8px;
			@media (max-width: 480px) {
				justify-content: center;
			}
		}

		&-btn {
			height: 42px !important;
			padding: 0 38px !important;
			font-size: 18px;
			font-weight: 700;
			line-height: 21.94px;
			text-transform: capitalize;
			font-family: 'Inter', sans-serif;
			color: var(--button-secondary-background-color) !important;
			border: 2px solid var(--button-secondary-background-color);
			background-color: var(--button-secondary-color) !important;
			border-radius: 8px;
		}
	}
}
</style>
